import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useContentToken,
} from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ---------------------------------------------------------------------------
// Weidner Via6 override of theme-rise PetPolicy.
//
// Trimmed down to just the stat cards. The original two-column layout
// (welcome badge, description, restrictions, CTA on the left; cards on
// the right) is gone — the section now uses its full width for an
// enlarged card grid rendered on a primary-color surface, with card
// content drawn in `textOnPrimary` for high contrast against the dark
// navy background.
// ---------------------------------------------------------------------------

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

// Max weight has special semantics:
//   - 0 / "none"     → render the card with the value "None"
//   - false / unset  → hide the card entirely
//   - anything else  → render "{value} lbs"
function getMaxWeightDisplay(val: unknown): string | null {
  if (val === null || val === undefined) return null;
  const s = String(val).trim().toLowerCase();
  if (s === '' || s === 'false' || s === 'null' || s === 'undefined') return null;
  if (s === '0' || s === 'none') return 'None';
  return `${val} lbs`;
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  // Stat values — defaultField in the schema pulls the property value;
  // authors can override per-site.
  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const maxWeightValue = useContentToken('petpolicy.max_weight_value');
  const breedRestrictionsValue = useContentToken('petpolicy.breed_restrictions_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const showMaxPets = hasValue(maxPetsValue);
  const maxWeightDisplay = getMaxWeightDisplay(maxWeightValue);
  const showMaxWeight = maxWeightDisplay !== null;
  const showBreedRestrictions = hasValue(breedRestrictionsValue);
  const showMonthlyRent = hasValue(monthlyRentValue);
  const showStats =
    showMaxPets || showMaxWeight || showBreedRestrictions || showMonthlyRent;

  if (!showStats) return null;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pt-4 pb-16 md:pt-6 md:pb-24 bg-[var(--vibe-color-primary)]"
    >
      <div className="w-full px-4 sm:px-8 lg:px-16">
        <div className="pet-stats">
          {showMaxPets && (
            <div className="pet-stat-card">
              <RichTextContent path="petpolicy.max_pets_label" as="span" className="pet-stat-label" />
              <EditableToken path="petpolicy.max_pets_value" as="span" className="pet-stat-value">
                {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
              </EditableToken>
            </div>
          )}
          {showMaxWeight && (
            <div className="pet-stat-card">
              <RichTextContent path="petpolicy.max_weight_label" as="span" className="pet-stat-label" />
              <EditableToken path="petpolicy.max_weight_value" as="span" className="pet-stat-value">
                {maxWeightDisplay ?? ''}
              </EditableToken>
            </div>
          )}
          {showBreedRestrictions && (
            <div className="pet-stat-card">
              <RichTextContent path="petpolicy.breed_restrictions_label" as="span" className="pet-stat-label" />
              <EditableToken path="petpolicy.breed_restrictions_value" as="span" className="pet-stat-value">
                {breedRestrictionsValue || ''}
              </EditableToken>
            </div>
          )}
          {showMonthlyRent && (
            <div className="pet-stat-card">
              <RichTextContent path="petpolicy.monthly_rent_label" as="span" className="pet-stat-label" />
              <EditableToken path="petpolicy.monthly_rent_value" as="span" className="pet-stat-value">
                {monthlyRentValue ? `${formatCurrency(monthlyRentValue)} per pet` : ''}
              </EditableToken>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pet-stats {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
        }
        @media (min-width: 640px) {
          .pet-stats {
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
          }
        }
        @media (min-width: 1024px) {
          .pet-stats {
            /* Auto-flow so 1–5 cards always stretch edge-to-edge. */
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 2rem;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2.5rem 1.5rem;
          border: 1px solid rgba(0, 0, 0, 0.06);
          background: #ffffff;
          border-radius: 6px;
          min-height: 200px;
          color: #000000;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18), 0 1px 3px rgba(0, 0, 0, 0.12);
        }

        .pet-stat-label {
          font-family: var(--vibe-font-body);
          font-size: 1.4rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #000000;
          margin-bottom: 0.85rem;
        }
        .pet-stat-label,
        .pet-stat-label * {
          font-weight: 400 !important;
          color: #000000 !important;
        }
        @media (min-width: 1024px) {
          .pet-stat-label {
            font-size: 1.6rem;
          }
        }

        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 1.35rem;
          font-weight: 400;
          color: #000000;
        }
        .pet-stat-value,
        .pet-stat-value * {
          font-weight: 400 !important;
          color: #000000 !important;
        }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: #000000;
          opacity: 0.75;
          margin-top: 0.3rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      description:
        'Maximum pet weight in lbs. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.breed_restrictions_label': {
      label: 'Breed Restrictions Label',
      type: 'richtext',
      defaultValue: '<p>Breed Restrictions</p>',
    },
    'petpolicy.breed_restrictions_value': {
      label: 'Breed Restrictions Value',
      type: 'text',
      defaultField: 'property_pet_restrictions',
      description:
        'Breed restrictions text. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      description:
        'Monthly pet rent amount. Pulled from the property automatically; override here to display a custom value.',
    },
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicy;
